import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/* eslint-disable no-console */

/**
 * Event handler for when a new client is added to the current bridge API instance.
 */

/**
 * Maintains a registry of all local and remote clients known to the current bridge API instance.
 *
 * Used to determine and mark the reachability of clients from other clients - e.g., if `rovo-chat`
 * can be reached from inside of a cross-origin `iframe` in a browser context.
 */
export var ClientRegistry = /*#__PURE__*/function () {
  function ClientRegistry() {
    _classCallCheck(this, ClientRegistry);
    /**
     * A registry of all clients known to the current bridge API instance.
     *
     * @description NOTE: Clients undergo state transitions from pending -> ready until the current bridge
     * API instance is able to communicate with them.
     */
    _defineProperty(this, "registry", new Map());
    /**
     * Event handlers for when clients are added to the registry.
     */
    _defineProperty(this, "onClientAddedHandlers", new Set());
  }
  return _createClass(ClientRegistry, [{
    key: "get",
    value:
    /**
     * Yields the current state of a client.
     * @param clientId
     * @returns current state
     */
    function get(clientId) {
      return this.registry.get(clientId);
    }

    /**
     * Yields whether a given client is known or not to the registry.
     * @param clientId unique identifier for the client
     */
  }, {
    key: "has",
    value: function has(clientId) {
      return this.registry.has(clientId);
    }

    /**
     * Adds an entry for a new, pending client. It will be marked as `ready` once a successful handshake transpires.
     *
     * @description Will early exit with a warning if the client being marked as `pending` is already `ready`. This
     * is an anomalous state transition which we do not expect.
     *
     * @param clientId unique identifier for the client
     * @returns void
     */
  }, {
    key: "addPendingClient",
    value: function addPendingClient(clientId) {
      var client = this.get(clientId);
      if (client && client.state === 'ready') {
        console.warn('tried to mark', clientId, 'as `pending` when already in `ready` state');
        return;
      }
      this.registry.set(clientId, {
        id: clientId,
        state: 'pending'
      });
    }

    /**
     * Adds an entry for a local client. It will be immediately ready for direct communication in the same JS context.
     *
     * @description Will early exit with a warning if the client being marked as `local` if already `remote`. This
     * is an anomalous state transition which we do not expect.
     *
     * @param clientId unique identifier for the client
     * @returns void
     */
  }, {
    key: "addLocalClient",
    value: function addLocalClient(clientId) {
      if (this.isRemoteClient(clientId)) {
        console.warn('tried to mark', clientId, 'as `local` when already tracked as `remote`');
        return;
      }
      var wasAlreadyPresent = this.has(clientId);
      this.registry.set(clientId, {
        id: clientId,
        type: 'local',
        state: 'ready'
      });

      // only emit the event if this is a new client
      if (!wasAlreadyPresent) {
        this.emitClientAdded(clientId);
      }
    }

    /**
     * Adds an entry for a remote client. It will be ready for asynchronous communication once a handshake transpires.
     *
     * @description Will early exit with a warning if the client being marked as `remote` if already `local`. This
     * is an anomalous state transition which we do not expect.
     *
     * @param clientId unique identifier for the client
     * @returns void
     */
  }, {
    key: "addRemoteClient",
    value: function addRemoteClient(clientId) {
      if (this.isLocalClient(clientId)) {
        console.warn('tried to mark', clientId, 'as `remote` when already tracked as `local`');
        return;
      }
      var wasAlreadyPresent = this.has(clientId);
      this.registry.set(clientId, {
        id: clientId,
        type: 'remote',
        state: 'ready'
      });

      // only emit the event if this is a new client
      if (!wasAlreadyPresent) {
        this.emitClientAdded(clientId);
      }
    }
  }, {
    key: "isPendingClient",
    value: function isPendingClient(clientId) {
      var client = this.get(clientId);
      return (client === null || client === void 0 ? void 0 : client.state) === 'pending';
    }
  }, {
    key: "isLocalClient",
    value: function isLocalClient(clientId) {
      var client = this.get(clientId);
      return (client === null || client === void 0 ? void 0 : client.state) === 'ready' && client.type === 'local';
    }
  }, {
    key: "isRemoteClient",
    value: function isRemoteClient(clientId) {
      var client = this.get(clientId);
      return (client === null || client === void 0 ? void 0 : client.state) === 'ready' && client.type === 'remote';
    }
  }, {
    key: "size",
    get: function get() {
      return this.registry.size;
    }

    /**
     * Removes a client from the registry.
     *
     * @param clientId unique identifier for the client to remove
     * @returns true if the client was removed, false if it wasn't found
     */
  }, {
    key: "removeClient",
    value: function removeClient(clientId) {
      return this.registry.delete(clientId);
    }

    /**
     * Gets all client IDs currently in the registry.
     *
     * @returns array of all client IDs
     */
  }, {
    key: "getAllClientIds",
    value: function getAllClientIds() {
      return Array.from(this.registry.keys());
    }

    /**
     * Register a handler to be called when a client is added to the registry.
     *
     * @param handler Function to call when a client is added
     */
  }, {
    key: "onClientAdded",
    value: function onClientAdded(handler) {
      this.onClientAddedHandlers.add(handler);
    }

    /**
     * Emit client added event to all registered handlers.
     *
     * @param clientId The client ID that was added
     */
  }, {
    key: "emitClientAdded",
    value: function emitClientAdded(clientId) {
      var _iterator = _createForOfIteratorHelper(this.onClientAddedHandlers),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var handler = _step.value;
          try {
            handler(clientId);
          } catch (error) {
            console.error('Error in client added handler:', error);
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }

    /**
     * Clean up all event handlers.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      this.onClientAddedHandlers.clear();
      this.registry.clear();
    }
  }]);
}();